import React from 'react';
import {
  Typography,
  Box
} from '@mui/material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { TicketCommentView } from '../../../../../services/TicketService';
import { useNotesManagement } from '../../../hooks/useNotesManagement';
import { useAppDispatch } from '../../../../../app/hooks';
import { openViewNoteDialog } from '../../../state';

const NotesGrid: React.FC = () => {
  const dispatch = useAppDispatch();
  const { sortedNotes, hasNotes } = useNotesManagement();

  const handleRowSelect = (args: RowSelectEventArgs) => {
    const selectedNote = args.data as TicketCommentView;
    dispatch(openViewNoteDialog(selectedNote));
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Unknown';
    }
  };

  // Custom template for the date column
  const dateTemplate = (props: any) => {
    return <span>{formatDate(props.createdDate)}</span>;
  };

  if (!hasNotes) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
        sx={{ 
          backgroundColor: '#f8fafc',
          border: '1px dashed #e2e8f0',
          borderRadius: 1,
          maxWidth: 800
        }}
      >
        <Typography variant="body2" color="textSecondary">
          No notes have been added yet. Click "Add Note" to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography 
        variant="body2" 
        sx={{ 
          mb: 1, 
          color: '#64748b',
          fontStyle: 'italic'
        }}
      >
        Click on any row to view the full note
      </Typography>
      <Box
        sx={{
          maxWidth: '800px',
          '& .e-grid .e-content': {
            backgroundColor: '#ffffff',
          },
          '& .e-grid .e-headercontent': {
            backgroundColor: '#f8fafc',
            borderBottom: '2px solid #e5e7eb',
          },
          '& .e-grid .e-headercelldiv': {
            fontWeight: 600,
            color: '#374151',
            fontSize: '0.8rem',
            padding: '4px 8px',
          },
          '& .e-grid .e-rowcell': {
            padding: '2px 8px',
            fontSize: '0.8rem',
            lineHeight: 1.3,
          },
          '& .e-grid .e-row': {
            minHeight: '40px',
            height: '40px',
            borderBottom: '1px solid #f3f4f6',
          },
          '& .e-grid .e-row:hover': {
            backgroundColor: '#f0f9ff',
            cursor: 'pointer',
          },
          '& .e-grid .e-row.e-altrow:hover': {
            backgroundColor: '#f0f9ff',
          },
        }}
      >
        <GridComponent
          dataSource={sortedNotes}
          allowPaging={true}
          allowSorting={true}
          pageSettings={{ pageSize: 10 }}
          sortSettings={{
            columns: [{ field: 'createdDate', direction: 'Descending' as any }]
          }}
          rowSelected={handleRowSelect}
          height="300px"
          width="750px"
          gridLines="Both"
        >
          <ColumnsDirective>
            <ColumnDirective 
              field="comment" 
              headerText="Note" 
              width="400"
              textAlign="Left"
              allowSorting={false}
            />
            <ColumnDirective 
              field="author" 
              headerText="Author" 
              width="150"
              textAlign="Left"
              allowSorting={true}
            />
            <ColumnDirective 
              field="createdDate" 
              headerText="Created" 
              width="175"
              textAlign="Left"
              allowSorting={true}
              template={dateTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort]} />
        </GridComponent>
      </Box>
    </Box>
  );
};

export default NotesGrid;
